import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Star, 
  Plus, 
  Edit, 
  Trash2, 
  QrCode, 
  ExternalLink, 
  TrendingUp,
  Users,
  Gift,
  Eye,
  XCircle,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReviewBusinessForm from '../components/ReviewBusinessForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewManager = () => {
  // Helper to get auth token (supports both user and admin)
  const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('admin_token');
  
  const [hasAccess, setHasAccess] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [viewingQR, setViewingQR] = useState(null);
  const [viewingAnalytics, setViewingAnalytics] = useState(null);

  const [formData, setFormData] = useState({
    businessName: '',
    businessType: 'restaurant',
    description: '',
    email: '',
    logo: '',
    reviewLinks: [],
    socialLinks: [],
    couponOffers: [],
    primaryColor: '#3B82F6',
    active: true
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      await axios.get(`${API}/review-manager/access-check`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setHasAccess(true);
      loadBusinesses();
    } catch (error) {
      setHasAccess(false);
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/review-manager/businesses`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setBusinesses(response.data.businesses);
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.businessName || formData.reviewLinks.length === 0) {
      toast.error('Please fill in business name and add at least one review link');
      return;
    }

    try {
      if (editingBusiness) {
        await axios.put(
          `${API}/review-manager/businesses/${editingBusiness.id}`,
          formData,
          { headers: { Authorization: `Bearer ${getAuthToken()}` } }
        );
        toast.success('Business updated successfully!');
      } else {
        const response = await axios.post(
          `${API}/review-manager/businesses`,
          formData,
          { headers: { Authorization: `Bearer ${getAuthToken()}` } }
        );
        toast.success(`Business created! QR URL: ${response.data.qrCodeUrl}`);
      }

      // Ensure form closes and list refreshes
      setShowForm(false);
      setEditingBusiness(null);
      resetForm();
      
      // Reload businesses with a slight delay to ensure backend processing
      setTimeout(() => {
        loadBusinesses();
      }, 500);
    } catch (error) {
      console.error('Save business error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save business');
    }
  };

  const resetForm = () => {
    setFormData({
      businessName: '',
      businessType: 'restaurant',
      description: '',
      logo: '',
      reviewLinks: [],
      socialLinks: [],
      couponOffers: [],
      primaryColor: '#3B82F6',
      active: true
    });
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setFormData({
      businessName: business.businessName,
      businessType: business.businessType,
      description: business.description || '',
      email: business.email || '',
      logo: business.logo || '',
      reviewLinks: business.reviewLinks || [],
      socialLinks: business.socialLinks || [],
      couponOffers: business.couponOffers || [],
      primaryColor: business.primaryColor || '#3B82F6',
      active: business.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;

    try {
      await axios.delete(`${API}/review-manager/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success('Business deleted');
      loadBusinesses();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const viewQRCode = async (businessId) => {
    try {
      const response = await axios.get(
        `${API}/review-manager/businesses/${businessId}/qr`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setViewingQR(response.data);
    } catch (error) {
      toast.error('Failed to load QR code');
    }
  };

  const viewAnalytics = async (businessId) => {
    try {
      const response = await axios.get(
        `${API}/review-manager/businesses/${businessId}/analytics`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      setViewingAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  // Helper functions moved to ReviewBusinessForm component

  if (hasAccess === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-gray-600">
                This Review Manager is not available for your account.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Star className="w-8 h-8 text-yellow-500" />
                Review & Rewards Manager
              </h1>
              <p className="text-gray-600 mt-2">
                Manage business reviews, social links, and coupon rewards
              </p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingBusiness(null);
                setShowForm(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Business
            </Button>
          </div>
        </div>

        {/* Business Form */}
        {showForm && (
          <ReviewBusinessForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingBusiness(null);
              resetForm();
            }}
            isEditing={!!editingBusiness}
          />
        )}

        {/* Business List */}
        {loading && !showForm && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading businesses...</p>
          </div>
        )}

        {!showForm && businesses.length === 0 && !loading && (
          <Card>
            <CardContent className="text-center py-12">
              <Star className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Businesses Yet</h3>
              <p className="text-gray-500 mb-4">Get started by adding your first business</p>
              <Button
                onClick={() => {
                  resetForm();
                  setEditingBusiness(null);
                  setShowForm(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Business
              </Button>
            </CardContent>
          </Card>
        )}

        {!showForm && businesses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((business) => (
              <Card key={business.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="truncate">{business.businessName}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      business.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {business.active ? 'Active' : 'Inactive'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm text-gray-600">
                    <p className="capitalize">{business.businessType}</p>
                    <p className="mt-1">{business.description?.substring(0, 100)}...</p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-blue-50 p-2 rounded">
                      <Eye className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                      <p className="font-semibold">{business.totalScans || 0}</p>
                      <p className="text-gray-600">Scans</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <Star className="w-4 h-4 mx-auto mb-1 text-green-600" />
                      <p className="font-semibold">{business.totalReviews || 0}</p>
                      <p className="text-gray-600">Reviews</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded">
                      <Gift className="w-4 h-4 mx-auto mb-1 text-purple-600" />
                      <p className="font-semibold">{business.totalCouponsGiven || 0}</p>
                      <p className="text-gray-600">Coupons</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewQRCode(business.id)}
                      className="flex-1"
                    >
                      <QrCode className="w-3 h-3 mr-1" />
                      QR
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewAnalytics(business.id)}
                      className="flex-1"
                    >
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Stats
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(business)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(business.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  <a
                    href={`/review/${business.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Public Page
                  </a>
                </CardContent>
              </Card>
            ))}

            {businesses.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No businesses added yet</p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Business
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Form - CONTINUED IN NEXT PART DUE TO LENGTH */}
      </div>

      {/* QR Code Modal */}
      {viewingQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>QR Code: {viewingQR.businessName}</span>
                <Button variant="ghost" size="sm" onClick={() => setViewingQR(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <img src={viewingQR.qrCode} alt="QR Code" className="mx-auto" />
              <p className="text-sm text-gray-600">{viewingQR.url}</p>
              <Button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = viewingQR.qrCode;
                  link.download = `${viewingQR.businessName}-QR.png`;
                  link.click();
                }}
                className="w-full"
              >
                Download QR Code
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Modal */}
      {viewingAnalytics && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Analytics: {viewingAnalytics.businessName}</span>
                <Button variant="ghost" size="sm" onClick={() => setViewingAnalytics(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <Eye className="w-6 h-6 text-blue-600 mb-2" />
                  <p className="text-2xl font-bold">{viewingAnalytics.totalScans}</p>
                  <p className="text-sm text-gray-600">Total Scans</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <Star className="w-6 h-6 text-green-600 mb-2" />
                  <p className="text-2xl font-bold">{viewingAnalytics.totalReviews}</p>
                  <p className="text-sm text-gray-600">Reviews</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <Gift className="w-6 h-6 text-purple-600 mb-2" />
                  <p className="text-2xl font-bold">{viewingAnalytics.totalCouponsGiven}</p>
                  <p className="text-sm text-gray-600">Coupons</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-yellow-600 mb-2" />
                  <p className="text-2xl font-bold">{viewingAnalytics.conversionRate}%</p>
                  <p className="text-sm text-gray-600">Conversion</p>
                </div>
              </div>

              {viewingAnalytics.platformBreakdown && Object.keys(viewingAnalytics.platformBreakdown).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Platform Breakdown</h4>
                  <div className="space-y-2">
                    {Object.entries(viewingAnalytics.platformBreakdown).map(([platform, count]) => (
                      <div key={platform} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="capitalize">{platform}</span>
                        <span className="font-semibold">{count} reviews</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReviewManager;
